import { useEffect, useState } from "react";
import PaymentMethods from "@/components/PaymentMethods";
import { Seo } from "@/components/Seo";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Check, Car, Wrench, PackageCheck, Flag, Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const STEPS = ["recu", "en_cours", "pret", "livre"];
const ICONS = { recu: Car, en_cours: Wrench, pret: PackageCheck, livre: Flag };

const Track = () => {
  const { t, lang } = useLanguage();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (params.get("code")) search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await axios.get(`${API}/track/${encodeURIComponent(code.trim().toUpperCase())}`);
      setResult(r.data);
    } catch {
      setError(t.track.notFound);
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = result?.repair_status ? STEPS.indexOf(result.repair_status) : -1;

  return (
    <main data-testid="track-page">
      <Seo page="track" />
      <section className="relative overflow-hidden pb-16 pt-40">
        <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 rounded-full bg-mint blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHead as="h1" overline={t.track.overline} title={t.track.title} accent={t.track.titleAccent} sub={t.track.sub} />

          <Reveal delay={0.2}>
            <form onSubmit={search} className="mt-10 flex flex-col gap-3 sm:flex-row" data-testid="track-form">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t.track.placeholder}
                data-testid="track-code-input"
                className="flex-1 rounded-full border border-brand/20 bg-white px-6 py-4 font-mono text-base uppercase tracking-[0.2em] text-forest outline-none transition-all focus:border-gold focus:ring-4 focus:ring-gold/10"
              />
              <button type="submit" disabled={loading} data-testid="track-submit-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-xl shadow-gold/25 transition-all hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60">
                <Search className="h-4 w-4" />
                {t.track.button}
              </button>
            </form>
          </Reveal>

          {error && <p className="mt-6 rounded-2xl bg-cream px-6 py-4 text-sm font-semibold text-gold" data-testid="track-error">{error}</p>}

          {result && (
            <div className="mt-12 rounded-3xl border border-brand/10 bg-white p-8 shadow-xl shadow-brand/5 sm:p-10" data-testid="track-result">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{result.tracking_code}</p>
                  <h2 className="font-display mt-2 text-2xl font-extrabold text-forest">{t.track.hello} {result.name}</h2>
                </div>
                <div className="grid gap-1 text-sm text-forest sm:text-right">
                  {result.vehicle && <p><span className="font-mono text-[10px] uppercase tracking-widest text-brand">{t.track.vehicle}</span><br /><b>{result.vehicle}</b></p>}
                  <p><span className="font-mono text-[10px] uppercase tracking-widest text-brand">{t.track.service}</span><br />{result.service}</p>
                  {result.date && <p><span className="font-mono text-[10px] uppercase tracking-widest text-brand">{t.track.rdv}</span><br />{result.date}{result.time ? ` · ${result.time}` : ""}</p>}
                </div>
              </div>

              {(result.invoice_url || result.estimate_url) && (
                <div className="mt-6 flex flex-wrap gap-3" data-testid="track-documents">
                  {result.invoice_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${result.invoice_url}`} data-testid="track-invoice-download" className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-all hover:-translate-y-0.5 hover:bg-brand">{t.track_docs.invoice}{result.invoice_number ? ` · ${result.invoice_number}` : ""}</a>}
                  {result.estimate_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${result.estimate_url}`} data-testid="track-estimate-download" className="inline-flex items-center gap-2 rounded-full border border-brand/30 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand transition-colors hover:bg-mint">{t.track_docs.estimate}</a>}
                </div>
              )}
              <p className="mt-6 text-sm text-forest/80" data-testid="track-client-space-link"><Link to="/mon-espace" className="font-semibold text-gold underline">{t.client.title}</Link> — {t.client.subtitle}</p>
              {result.invoice_paid_at && (
                <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-forest" data-testid="track-invoice-paid">✔ {t.track_docs.paid} · {new Date(result.invoice_paid_at).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR")}</p>
              )}
              {result.invoice_url && !result.invoice_paid_at && <div className="mt-6"><PaymentMethods testid="track-payment-methods" /></div>}
              {result.pay_qrs?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-4" data-testid="track-pay-qrs">
                  {result.pay_qrs.map((s) => (
                    <a key={s.id} href={s.link || undefined} target={s.link ? "_blank" : undefined} rel="noopener noreferrer" data-testid={`track-pay-qr-${s.id}`} className="flex flex-col items-center gap-2 rounded-2xl border border-brand/10 bg-white p-4 text-center transition-transform hover:-translate-y-0.5">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${s.image}`} alt={`QR ${s.label}`} className="h-32 w-32 rounded-xl bg-white object-contain" />
                      <span className="text-xs font-semibold text-forest">{t.track_docs.scan} {s.label}</span>
                    </a>
                  ))}
                </div>
              )}

              {currentIdx < 0 ? (
                <p className="mt-8 flex items-center gap-3 rounded-2xl bg-mint px-5 py-4 text-sm text-forest" data-testid="track-pending">
                  <Clock className="h-5 w-5 shrink-0 text-brand" /> {t.track.pending}
                </p>
              ) : (
                <ol className="mt-10 grid gap-4 sm:grid-cols-4" data-testid="track-steps">
                  {STEPS.map((s, i) => {
                    const Icon = ICONS[s];
                    const done = i < currentIdx;
                    const current = i === currentIdx;
                    return (
                      <li key={s} className="relative" data-testid={`track-step-${s}`} data-state={done ? "done" : current ? "current" : "todo"}>
                        {i < STEPS.length - 1 && <span className={`absolute left-1/2 top-7 hidden h-0.5 w-full sm:block ${i < currentIdx ? "bg-brand" : "bg-brand/15"}`} />}
                        <div className="relative flex flex-col items-center text-center">
                          <span className={`flex h-14 w-14 items-center justify-center rounded-2xl transition-all ${done ? "bg-brand text-white" : current ? "animate-pulse-ring bg-gold text-white shadow-xl shadow-gold/30" : "bg-mint text-brand/40"}`}>
                            {done ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
                          </span>
                          <p className={`font-display mt-4 text-sm font-bold ${current ? "text-gold" : done ? "text-forest" : "text-brand/50"}`}>{t.track.steps[s]}</p>
                          {current && <p className="mt-1 text-xs text-brand-hover">{t.track.stepsDesc[s]}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}

              {result.history?.length > 0 && (
                <div className="mt-10 border-t border-brand/10 pt-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{t.track.history}</p>
                  <ul className="mt-3 space-y-2 text-sm text-forest">
                    {[...result.history].reverse().map((h, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
                        <span className="font-mono text-xs text-brand">{new Date(h.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        {t.track.steps[h.status]}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Track;
